import * as Pipeline from '../core/utils/pipeline';
import { BaseComponent } from '../core/interfaces/BaseComponent';
import { Component, Element, Prop, VNode, Watch, h } from '@stencil/core';
import { Hyperlink, getHyperLinkObject } from '../core/interfaces/Hyperlink';
import { Theme } from '../core/types/globalTypes';
import { generateId } from '../core/utils/components';
import { generateSVGImage } from 'utils/imageHelper';
import { height, svgPathData, width } from '@fortawesome/pro-light-svg-icons/faChevronRight';

/**
 * The call to action link is used to combination of anchor text and a chevron to provide greater affordance
 * for the most important link related to a set of content. Avoid using too many CTA links in proximity,
 * for example in lists. Instead use a single CTA link per content section or card.
 * @tag `<ads-cta-link>`
 * @slot - Content rendered as rich text inside the component
 * @example <ads-cta-link link="https://www.arm.com">Learn More</ads-cta-link>
 */
@Component({
  tag: 'ads-cta-link',
  styleUrl: 'ads-cta-link.scss',
  shadow: true,
})
export class AdsCtaLink implements BaseComponent {
  /**
   * @hidden
   */
  public component: string;

  constructor() {
    this.el.setAttribute('id', this.identifier || generateId('ads-cta-link'));
    this.component = 'ads-cta-link';
  }

  /**
   * @hidden
   */
  @Element() el: HTMLAdsCtaLinkElement;

  /**
   * The unique identifier (optional)
   */
  @Prop({ attribute: 'id' }) identifier?: string;

  /**
   * The theme for this component instance (optional)
   * Values: "default", "dark"
   */
  @Prop({ reflect: true }) theme?: Theme;

  /**
   * Contains a URL (fragment).
   * If this property is set, an anchor tag will be rendered.
   */
  @Prop({ reflect: true }) link: Hyperlink;

  /**
   * Text to display as the aria-label (optional)
   */
  @Prop({ reflect: true }) srLabel?: string;

  /**
   * Handle identifier property changes
   * @param {string} newValue The new value for "identifier"
   * @hidden
   */
  @Watch('identifier')
  handleIdentifierChange(newValue: string): void {
    this.el.setAttribute('id', newValue || generateId('ads-cta-link'));
  }

  protected componentDidLoad(): void {
    Pipeline.addComponent(this.el);
  }

  protected disconnectedCallback(): void {
    Pipeline.removeComponent(this.el);
  }

  protected render(): VNode {
    const linkObj = getHyperLinkObject(this.link);

    const linkAttributes = {
      href: linkObj?.href,
      hreflang: linkObj?.hreflang || null,
      rel: linkObj?.rel || null,
      target: linkObj?.target || null,
      tabIndex: 0,
      'aria-label': this.srLabel,
      onClick: linkObj?.onClick || null,
      'data-track': 'true',
      'data-track-value': this.srLabel,
    };

    return (
      <span class="c-cta-link">
        <a class="c-cta-link__anchor" {...linkAttributes}>
          <slot />
        </a>
        <span class="c-cta-link__icon" aria-hidden="true">
          {generateSVGImage({ data: svgPathData, height, width })}
        </span>
      </span>
    ) as VNode;
  }
}
